import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { touchPrompt } from '../phrases';
import { SceneView } from '../scenes/SceneView';
import { sceneOf, type ScenePart } from '../scenes/scenes';
import { KINDS } from '../../concepts/kinds';

function SceneFinder({ props, kind, verb }: { props: GameProps; kind: 'findPart' | 'findInScene'; verb: string }) {
  const game = useGame(props);
  const scene = sceneOf(game.config?.scene);
  const wanted = Array.isArray(game.config?.parts) ? (game.config!.parts as string[]) : [];
  const targets = scene ? scene.parts.filter((p) => wanted.includes(p.id)) : [];
  const [target, setTarget] = useState<ScenePart | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [misses, setMisses] = useState(0);
  const [done, setDone] = useState(false);
  const queue = useRef<ScenePart[]>([]);
  const busy = useRef(false);
  const fixed = game.str('trialOrder', 'random') === 'fixed';

  const start = useCallback(async () => {
    if (!scene || targets.length === 0) return;
    if (queue.current.length === 0) queue.current = fixed ? [...targets] : shuffle(targets);
    const next = queue.current.shift() ?? pick(targets);
    setDone(false);
    setMisses(0);
    const id = await game.register({ scene: scene.id, prompt: next.id }, { value: next.id });
    if (!id) return;
    setTarget(next);
    setTrial(id);
  }, [scene?.id, wanted.join(), fixed]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);

  const prompt = target ? touchPrompt(verb, target.label) : '';
  useEffect(() => {
    if (!target || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(prompt), 300);
    return () => window.clearTimeout(t);
  }, [target, trial]);

  async function touch(id: string) {
    if (!target || !trial || done || busy.current) return;
    if (id !== target.id && game.errorless) return;
    if (id !== target.id) {
      setMisses((m) => m + 1);
      game.say(prompt);
      busy.current = true;
      await game.respond(trial, id);
      const fresh = await game.register({ scene: scene!.id, prompt: target.id, retry: true }, { value: target.id });
      busy.current = false;
      if (fresh) setTrial(fresh);
      return;
    }
    busy.current = true;
    const v = await game.respond(trial, id);
    busy.current = false;
    if (v) setDone(true);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });

  return (
    <GameFrame
      game={game}
      empty={!scene || targets.length === 0}
      emptyText={`${KINDS[kind].describes} Edit it to choose a picture and ${kind === 'findPart' ? 'its parts' : 'what to find'}.`}
      onResume={() => { void start(); }}
      breakTrial={trial}
    >
      {scene && target && (
        <div className={`g-board g-find${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={prompt} />
          <SceneView
            scene={scene}
            onPart={(id) => void touch(id)}
            hits={game.errorless ? [target.id] : undefined}
            outline={done || misses >= 2 ? target.id : null}
          />
        </div>
      )}
    </GameFrame>
  );
}

export default function FindPart(props: GameProps) {
  return <SceneFinder props={props} kind="findPart" verb="Touch" />;
}

export function FindInScene(props: GameProps) {
  return <SceneFinder props={props} kind="findInScene" verb="Find" />;
}
